import React, { useEffect, useState, useRef } from "react";
import classes from "./AboutService.module.css";
import { useServiceErstellen } from "../../../store/serviceErstellenStore";
import ErrorMessage from "../../../components/StudioErstellen/ErrorMessage";

const videoTags = [
    "Videografie", "Videography", "Kameraführung", "Camera Operation", "Content Creation", "Social Media Video", "Musikvideoproduktion", "Music Video Production", "Werbefilm", "Commercial Video", "Imagefilm", "Corporate Video", "Livestreaming", "Kurzfilm", "Short Film", "Dokumentarfilm", "Documentary", "Drohnenaufnahmen", "Drone Footage", "Behind-the-Scenes", "Farbkorrektur", "Color Grading", "Postproduktion", "Post Production", "VFX", "Visual Effects", "Storyboard", "Storyboarding", "Videoschnitt", "Video Editing", "Premiere Pro", "Final Cut Pro", "DaVinci Resolve"
]

const fotoTags = [
    "Fotografie", "Photography", "Portraitfotografie", "Portrait Photography", "Modefotografie", "Fashion Photography", "Eventfotografie", "Event Photography", "Produktfotografie", "Product Photography", "Hochzeitsfotografie", "Wedding Photography", "Architekturfotografie", "Architectural Photography", "Studiofotografie", "Studio Photography", "Streetfotografie", "Street Photography", "Reisefotografie", "Travel Photography", "Foodfotografie", "Food Photography", "Beautyfotografie", "Beauty Photography", "Editorialfotografie", "Editorial Photography", "Dokumentarfotografie", "Documentary Photography", "Tierfotografie", "Animal Photography", "Retusche", "Retouching", "Fotobearbeitung", "Photo Editing", "Portfolioerstellung", "Portfolio Curation"
]

const musicTags = [
    "Musikproduktion", "Music Production", "Beatproduktion", "Beatmaking", "Mixing", "Mastering", "Vocal Recording", "Audio Engineering", "Sounddesign", "Podcastproduktion", "Songwriting", "Arrangement", "Komposition", "MIDI Programmierung", "Synth Programmierung", "Studioaufnahme", "Vocal Editing", "Ableton", "Logic Pro", "FL Studio", "Foley", "Field Recording", "Samplepack Erstellung", "Musikcoaching", "Music Coaching"
]

export const AboutService = ({ validate }) => {
    const { serviceData, updateServiceData } = useServiceErstellen();
    const [hasSelectedSkills, setHasSelectedSkills] = useState(false);
    const [isDescriptionOverLimit, setIsDescriptionOverLimit] = useState(false);

    useEffect(() => {
        setHasSelectedSkills(serviceData?.skills?.length > 0 ? true : false);
    }, [serviceData?.skills]);

    const [ errors, setErrors ] = useState({
        description: "",
        skills: ""
    });
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    // Get available tags based on creativeCategories
    // creativeCategories can be: "music", "foto", "foto,video", or ""
    const getAvailableTags = () => {
        const availableTags = [];
        const creativeCategories = serviceData?.creativeCategories || "";
        const creativeTypes = creativeCategories ? creativeCategories.split(",") : [];

        if (creativeTypes.includes("music")) {
            availableTags.push(...musicTags);
        } else {
            if (creativeTypes.includes("foto")) {
                availableTags.push(...fotoTags);
            }
            if (creativeTypes.includes("video")) {
                availableTags.push(...videoTags);
            }
        }

        // Remove duplicates and sort
        return [...new Set(availableTags)].sort();
    };

    // Filter tags based on search query
    const getFilteredTags = () => {
        const availableTags = getAvailableTags();
        if (!searchQuery.trim()) {
            return availableTags;
        }
        const query = searchQuery.toLowerCase();
        return availableTags.filter(tag => 
            tag.toLowerCase().includes(query)
        );
    };

    // Handle tag selection
    const handleTagToggle = (tag) => {
        const currentSkills = serviceData?.skills || [];
        const isSelected = currentSkills.includes(tag);
        
        let newSkills;
        if (isSelected) {
            newSkills = currentSkills.filter(s => s !== tag);
        } else {
            newSkills = [...currentSkills, tag];
        }
        
        updateServiceData('skills', newSkills);
        // Clear search input after selecting from the list
        if (!isSelected) {
            setSearchQuery("");
        }

        // Clear validation error when user selects/removes skills
        if (errors.skills && newSkills.length >= 3) {
            setErrors(prev => ({ ...prev, skills: '' }));
        }
    };

    // Handle tag removal from selected chips
    const handleTagRemove = (tag) => {
        const currentSkills = serviceData?.skills || [];
        const newSkills = currentSkills.filter(s => s !== tag);
        updateServiceData('skills', newSkills);

        // Clear validation error when user removes skills if still valid
        if (errors.skills && newSkills.length >= 3) {
            setErrors(prev => ({ ...prev, skills: '' }));
        }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
                inputRef.current && !inputRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
                setSearchQuery("");
            }
        };

        if (isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isDropdownOpen]);

    const handleValidation = () => {
        let isValid = true;
        const newErrors = {
            description: "",
            skills: ""
        };

        // Validate description
        if (!serviceData?.description?.trim()) {
            newErrors.description = "Du musst eine Beschreibung für deinen Service eingeben.";
            isValid = false;
        } else if (serviceData?.description?.trim().length < 10) {
            newErrors.description = "Du musst mindestens 10 Zeichen für deine Beschreibung eingeben.";
            isValid = false;
        }

        // Validate skills
        const skills = serviceData?.skills || [];
        if (skills.length < 3) {
            newErrors.skills = "Du musst mindestens 3 Skills & Services auswählen.";
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    useEffect(() => {
        validate.current = handleValidation;
    }, [validate, serviceData?.description, serviceData?.skills]);

    // Validate skills when they change
    useEffect(() => {
        const skills = serviceData?.skills || [];
        if (skills.length < 3 && skills.length > 0) {
            // Only show error if user has started selecting but hasn't reached minimum
            setErrors(prev => {
                if (prev.skills) return prev; // Already has error, don't update
                return { ...prev, skills: 'Du musst mindestens 3 Skills & Services auswählen.' };
            });
        } else if (skills.length >= 3) {
            // Clear error when valid
            setErrors(prev => {
                if (!prev.skills) return prev; // No error, don't update
                return { ...prev, skills: '' };
            });
        }
    }, [serviceData?.skills]);

    const handleDescriptionChange = (e) => {
        const value = e.target.value;

        // Hard limit to 500 characters, soft limit (warning) at 100 characters
        if (value.length > 500) {
            return;
        }

        updateServiceData('description', value);
        setIsDescriptionOverLimit(value.length > 100);

        // Validate and set error
        if (!value.trim()) {
            setErrors(prev => ({ ...prev, description: 'Du musst eine Beschreibung für deinen Service eingeben.' }));
        } else if (value.trim().length < 10) {
            setErrors(prev => ({ ...prev, description: 'Du musst mindestens 10 Zeichen für deine Beschreibung eingeben.' }));
        } else {
            // Clear validation error when valid
            setErrors(prev => {
                if (!prev.description) return prev; // No error, don't update
                return { ...prev, description: '' };
            });
        }
    };

    const handleInputFocus = () => {
        setIsDropdownOpen(true);
    };

    const handleInputChange = (e) => {
        setSearchQuery(e.target.value);
        setIsDropdownOpen(true);
    };

    const selectedSkills = serviceData?.skills || [];
    const filteredTags = getFilteredTags();

    return (
        <div className={classes.mainContainer}>
            <header className={classes.header}>
                <h2 className="titleSemibold2">Erzähle was über dich.</h2>
                <p className="slimRegularLargeText" style={{color: 'var(--dark-grey-4)'}}>Beschreibe, wer du bist und was deine kreative Arbeit auszeichnet. 
                So finden die richtigen Leute deinen Service.</p>
            </header>
            <div className={classes.mainContent}>
                <div className={classes.aboutServiceDescriptionContainer}>
                    <h3 className="semiboldMiddle">Beschreibung</h3>
                    <textarea 
                        className={`${classes.aboutServiceTextarea} regularMiddle ${errors.description ? classes.inputError : ''} ${isDescriptionOverLimit ? classes.textareaLimitExceeded : ''}`} 
                        placeholder={
                            serviceData?.creativeCategories?.includes("video") ||
                            serviceData?.creativeCategories?.includes("foto")
                                ? "Beispiel: Videograf für Imagefilme, Musikvideos und Social Content."
                                : serviceData?.creativeCategories?.includes("music")
                                    ? "Beispiel: Producer für Recording, Mixing und Mastering."
                                    : ""
                        }
                        value={serviceData?.description || ''} 
                        onChange={handleDescriptionChange}
                    />
                    <div className={classes.inputFooter}>
                        {errors.description && (
                            <ErrorMessage>{errors.description}</ErrorMessage>
                        )}
                        {isDescriptionOverLimit && !errors.description && (
                            <ErrorMessage>Es sind maximal 100 Zeichen empfohlen.</ErrorMessage>
                        )}
                        <div className={`${classes.textCounter} slimRegularSmallText`}>
                            {Math.min(serviceData?.description?.length || 0, 100)} / 100
                        </div>
                    </div>
                </div>
                <div className={classes.aboutServiceSkillsContainer}>
                    <h3 className="semiboldMiddle">Skills & Services </h3>
                    <div className={classes.skillsInputWrapper}>
                        {hasSelectedSkills && (
                            <div className={classes.selectedTagsContainer}>
                                {selectedSkills.map((tag) => (
                                    <div onClick={() => handleTagRemove(tag)} key={tag} className={classes.selectedTag}>
                                        <span className="regularMiddle">{tag}</span>
                                        <svg className={classes.removeTagButton} xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                            <path d="M9 3L3 9M3 3L9 9" stroke="#77808A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div 
                            className={`${classes.aboutServiceSkillsInputContainer} ${hasSelectedSkills ? classes.hasSelectedTags : ''}`}
                        >
                            <svg className={classes.searchIcon} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path d="M13.7311 12.3972C14.7427 11.0611 15.3035 9.41345 15.3035 7.72566C15.3035 3.466 11.8683 0 7.65177 0C3.43529 0 0 3.466 0 7.72566C0 11.9853 3.42527 15.4413 7.65177 15.4413C9.32435 15.4413 10.9569 14.8887 12.2889 13.864L18.0678 19.5L19.5 18.0232L13.7311 12.3972ZM7.89214 13.3818H7.65177C4.55701 13.3818 2.04314 10.84 2.04314 7.71561C2.04314 4.59119 4.55701 2.06955 7.65177 2.06955C10.7465 2.06955 13.2504 4.60124 13.2504 7.72566C13.2504 10.8501 10.8968 13.2512 7.89214 13.3818Z" fill="#77808A"/>
                            </svg>
                            <input 
                                ref={inputRef}
                                className={classes.aboutServiceSkillsInput} 
                                type="text" 
                                placeholder="Skills & Services hinzufügen"
                                value={searchQuery}
                                onChange={handleInputChange}
                                onFocus={handleInputFocus}
                            />
                        </div>
                        {isDropdownOpen && (
                            <div ref={dropdownRef} className={classes.dropdownContainer}>
                                <div className={classes.dropdownContent}>
                                    {filteredTags.length > 0 ? (
                                        filteredTags.map((tag) => {
                                            const isSelected = selectedSkills.includes(tag);
                                            return (
                                                <div
                                                    key={tag}
                                                    className={classes.dropdownOption}
                                                    onClick={() => handleTagToggle(tag)}
                                                >
                                                    <span className="slimRegularSmallText">{tag}</span>
                                                    <input
                                                        className={classes.tagCheckbox}
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => handleTagToggle(tag)}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div className={classes.noResults}>
                                            <span className="slimRegularSmallText">Keine Ergebnisse gefunden</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className={classes.skillsFooter}>
                        {errors.skills && (
                            <ErrorMessage>{errors.skills}</ErrorMessage>
                        )}
                        <span className="slimRegularSmallText">Wähle mindestens 3 Skills & Services aus</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AboutService;